
import React, { Fragment } from 'react'

// Custom components
import { SelectInput } from '../../../SelectInput'
import { Grid, Tooltip } from '@mui/material'

// Styles
import {
  Area, Container, ErrorText, Form, GridCenter, IconArea, Image, ImageContainer, InfoContainer,
  InfoText,
  UploadButton,
  ContainerLeft,
  ContainerRight,
  ContainerInput,
  ContainerButtons,
  ContainerImage,
  RowContainer,
} from './styles';

import { CustomButtonV2, CustomFormLabel, CustomTextField } from '../../../../pages/styles';

// Icons
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ImageNotSupported, Info } from '@mui/icons-material';

// Utils
import { ColorsContext } from "../../../../Context/ColorsContext"
import { useTranslation } from 'react-i18next'

export const PlotPlanForm = ({
  plotPlanRef,
  form,
  errors,
  plotPlans,
  categories,
  areaTypes,
  handleChange,
  handleClickImage,
  handleSubmit,
  handleBack,
  loadingChildren,
  avoidEnterKey,
}) => {
  const { t } = useTranslation();
  const { colors } = React.useContext(ColorsContext);

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <ContainerLeft>
          <RowContainer>
            <ContainerInput>
              <CustomFormLabel>{t('plotPlans.plotPlanForm.name')}:</CustomFormLabel>
              <CustomTextField
                name='name'
                value={form.name}
                size="small"
                onChange={e => handleChange(e, 'name')}
                onKeyDown={avoidEnterKey}
                error={!(!errors.name.error)}
                helperText={errors.name.error}
                required
              />
            </ContainerInput>

            <ContainerInput>
              <CustomFormLabel>{t('plotPlans.plotPlanForm.image')}:</CustomFormLabel>
              <UploadButton component="label" startIcon={<CloudUploadIcon />} sx={{ color: colors.buttonActionColor }}>
                {t('plotPlans.plotPlanForm.uploadImage')}
                <input type="file" accept=".jpeg, .jpg, .png" hidden onChange={e => handleChange(e, 'image')} />
              </UploadButton>
              <span style={{ marginLeft: 10, fontSize: 14 }}>{form.image}</span>
              {errors.image.error && <ErrorText>{errors.image.error}</ErrorText>}
            </ContainerInput>
          </RowContainer>

          <RowContainer>
            <ContainerInput sx={{ width: {xs: '100%', sm: form.parentNodeLevel >= 1 ? '50%' : '100%', md: '80%'} }}>
              <CustomFormLabel>{t('plotPlans.plotPlanForm.parentNode')}:</CustomFormLabel>
              <SelectInput
                name='parent'
                onChange={e => handleChange(e, 'parent')}
                avoidEnterKey={true}
                value={form.parent}
                keyValue="nodePath"
                options={plotPlans}
                error={errors.parent.error}
                size="small"
                width="100%"
                disabled={form.isEdit}
              />
            </ContainerInput>

            {form.parentNodeLevel >= 1 &&
              <ContainerInput sx={{ width: {xs: '100%', sm: '50%', md: '80%'} }}>
                <CustomFormLabel>{t('plotPlans.plotPlanForm.areaType')}:</CustomFormLabel>
                <SelectInput
                  name='areaType'
                  onChange={e => {
                    handleChange({target: {value: e, type: 'text'}}, 'areaType')
                  }}
                  avoidEnterKey={true}
                  value={form.areaType}
                  keyValue="name"
                  options={areaTypes}
                  error={errors.areaType?.error}
                  size="small"
                  width="100%"
                />
              </ContainerInput>
            }
          </RowContainer>

          {form.parent?.id === 1 &&
						<RowContainer>
							<ContainerInput>
								<CustomFormLabel>{t('plotPlans.plotPlanForm.category')}:</CustomFormLabel>
								<SelectInput
									name='category'
									onChange={e => {
										handleChange({target: {value: e, type: 'text'}}, 'category')
									}}
                  avoidEnterKey={avoidEnterKey}
									value={form.category}
									keyValue="name"
									options={categories}
									error={errors.category.error}
									size="small"
									width="100%"
								/>
							</ContainerInput>
							<ContainerInput>
								<CustomFormLabel>{t('plotPlans.plotPlanForm.email')}:</CustomFormLabel>
								<CustomTextField
									value={form.email}
									size="small"
									type="email"
									onChange={e => handleChange(e, 'email')}
                  onKeyDown={avoidEnterKey}
									error={!(!errors.email.error)}
									helperText={errors.email.error}
								/>
							</ContainerInput>
						</RowContainer>
          }

          <RowContainer>
            <ContainerInput>
                <CustomFormLabel>{t('plotPlans.plotPlanForm.xPosition')}:</CustomFormLabel>
                <CustomTextField
                  value={form.parentX || 0}
                  size="small"
                  type="number"
                onChange={e => handleChange(e, 'parentX')}
                onKeyDown={avoidEnterKey}
                error={!(!errors.parentX.error)}
                helperText={errors.parentX.error}
                required
                disabled
              />
            </ContainerInput>

            <ContainerInput>
              <CustomFormLabel>{t('plotPlans.plotPlanForm.yPosition')}:</CustomFormLabel>
              <CustomTextField
                value={form.parentY || 0}
                size="small"
                type="number"
                onChange={e => handleChange(e, 'parentY')}
                onKeyDown={avoidEnterKey}
                error={!(!errors.parentY.error)}
                helperText={errors.parentY.error}
                required
                disabled
              />
            </ContainerInput>
          </RowContainer>

          <ContainerInput sx={{ width: {xs: '100%', sm: '50%', md: '80%'} }}>
            <CustomFormLabel>{t('plotPlans.height')}:</CustomFormLabel>
            <CustomTextField
              value={form.height || 0}
              size="small"
              type="number"
              onChange={e => handleChange(e, 'height')}
              onKeyDown={avoidEnterKey}
              error={!(!errors.height.error)}
              helperText={errors.height.error}
              required
            />
          </ContainerInput>

          <ContainerButtons>
            <Grid item xs={6}>
              <CustomButtonV2
                btnType="cancel"
                onClick={handleBack}>
                {t('common.cancel')}
              </CustomButtonV2>
            </Grid>
            <Grid item xs={6}>
              <CustomButtonV2
                btnType="primary"
                onClick={handleSubmit}>
                {t('common.save')}
              </CustomButtonV2>
            </Grid>
          </ContainerButtons>
        </ContainerLeft>

        <ContainerRight>
          {!loadingChildren &&
            <ContainerImage id="CONTAINER IMAGE ">
              {form.parentImg &&
                <InfoContainer>
                  <Info sx={{ color: colors.main }} />
                  <InfoText>{t('plotPlans.plotPlanForm.locateNode')}</InfoText>
                </InfoContainer>
              }
              <ImageContainer>
                {
                  !form.parentImg ?
                    <GridCenter container direction="column">
                      <ImageNotSupported sx={{ color: colors.grayFont, width: '70px', height: '70px' }} />
                      <CustomFormLabel>{t('common.empty.noImage')}</CustomFormLabel>
                    </GridCenter>
                    :
                    <Fragment>
                      <Image
                        id="img-plot"
                        src={form.parentImg}
                        onClick={handleClickImage}
                        ref={plotPlanRef}
                      />
                      {form.children?.map((obj, index) => (
                        <Area
                          key={"child-area-" + index}
                          top={obj.coordY}
                          left={obj.coordX}
                          zIndex={obj.height}
                        >
                          {obj.element &&
                            <Tooltip title={
                              <span>{obj.name}<br></br>
                                {t('plotPlans.height')}: {obj.height}m</span>
                            }>
                              <IconArea selected={obj.isEdit && (obj.id === form.id)}>{obj.element}</IconArea>
                            </Tooltip>
                          }
                        </Area>
                      ))}
                    </Fragment>
                }
              </ImageContainer>
            </ContainerImage>
          }
        </ContainerRight>
      </Form>
    </Container>
  )
}
